/**
 * @file ProductGallerySection.tsx
 * @description Галерея товара (phase08.5 shadcn).
 */

import { Camera, ImageIcon, Star, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import {
  catalogMedia,
  type ProductMedia,
} from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import { SectionCard } from "./SectionCard";

interface Props {
  productId: string;
}

export function ProductGallerySection({ productId }: Props) {
  const notify = useNotify();
  const fileRef = useRef<HTMLInputElement>(null);
  const [media, setMedia] = useState<ProductMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setMedia(await catalogMedia.list(productId));
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить медиа");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function upload(files: FileList | File[]) {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        await catalogMedia.upload(productId, f);
      }
      notify.success("Загружено");
      await refresh();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "Не удалось загрузить");
    } finally {
      setUploading(false);
    }
  }

  async function onDelete(m: ProductMedia) {
    if (!confirm("Удалить файл?")) return;
    try {
      await catalogMedia.remove(productId, m.id);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  async function onSetPrimary(m: ProductMedia) {
    try {
      await catalogMedia.reorder(productId, [{ id: m.id, isPrimary: true }]);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  const sorted = [...media].sort((a, b) => a.sortOrder - b.sortOrder);
  const isFull = media.length >= 15;

  return (
    <SectionCard
      icon={<ImageIcon />}
      title="Фото"
      action={
        <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={isFull} loading={uploading}>
          Загрузить
        </Button>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) {
            void upload(e.target.files);
            e.target.value = "";
          }
        }}
      />
      {loading ? (
        <p className="type-caption text-on-surface-variant">Загрузка…</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {!isFull ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length > 0)
                  void upload(e.dataTransfer.files);
              }}
              className="aspect-square rounded-md border-2 border-dashed border-outline-variant flex flex-col items-center justify-center text-on-surface-muted cursor-pointer transition-colors hover:border-primary hover:text-primary"
            >
              <Camera className="size-8" />
              <span className="type-body mt-1 font-medium">Добавить фото</span>
            </button>
          ) : null}
          {sorted.map((m) => (
            <div
              key={m.id}
              className={`group relative aspect-square rounded-md overflow-hidden bg-background ${m.isPrimary ? "ring-2 ring-primary" : ""}`}
            >
              <img
                src={m.url}
                alt={m.alt ?? ""}
                className="w-full h-full object-cover"
              />
              {m.isPrimary ? (
                <div className="absolute top-1.5 left-1.5">
                  <Badge variant="primary">Главное</Badge>
                </div>
              ) : null}
              {/* v0.19.2 — кнопки видимы всегда (hover-оверлей был недоступен
                  на touch и неочевиден на desktop; фидбек пользователя). */}
              <div className="absolute bottom-1.5 right-1.5 flex gap-1.5">
                {!m.isPrimary ? (
                  <button
                    type="button"
                    onClick={() => void onSetPrimary(m)}
                    className="size-12 lg:size-11 rounded-full bg-surface-lowest shadow flex items-center justify-center hover:bg-surface-low transition-colors"
                    title="Сделать главным (обложка на витрине)"
                    aria-label="Сделать главным"
                  >
                    <Star className="size-4 text-on-surface" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void onDelete(m)}
                  className="size-12 lg:size-11 rounded-full bg-surface-lowest shadow text-destructive flex items-center justify-center hover:bg-destructive/10 transition-colors"
                  title="Удалить"
                  aria-label="Удалить фото"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="type-caption mt-2 text-on-surface-variant">
        JPG/PNG/WebP до 5 MB, макс. 15 файлов. Рекомендуемый размер —
        квадрат 1000×1000 (мин. 800×800): на витрине фото показывается
        квадратом (сетка каталога и галерея карточки), не-квадратные
        кадрируются по центру. Обложка на витрине — фото с бейджем
        «Главное»; звёздочка на любом фото делает главным его.
      </p>
    </SectionCard>
  );
}
